// Shared Google Identity Services (GSI) helper.
// Loads the GSI script once, initializes with auto-select enabled,
// and exposes a silent-renewal helper so a signed-in user stays signed in
// across visits without having to click the button again.

let scriptPromise = null;
let initialized = false;
let currentCallback = null;

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/** Loads the GSI client script exactly once. */
export function ensureGoogleLoaded() {
  if (window.google && window.google.accounts) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.body.appendChild(script);
  });
  return scriptPromise;
}

/**
 * Initializes GSI with the given credential callback.
 * auto_select lets Google return a fresh credential silently when the user
 * is already signed in and previously consented.
 */
export function initGoogleAuth(callback) {
  currentCallback = callback;
  if (!window.google || !window.google.accounts) return;
  if (initialized) return;

  window.google.accounts.id.initialize({
    client_id: CLIENT_ID,
    callback: (response) => {
      if (currentCallback) currentCallback(response);
    },
    auto_select: true,
    itp_support: true,
    cancel_on_tap_outside: false,
  });
  initialized = true;
}

/**
 * Attempts a silent sign-in via One Tap. If the user is eligible, Google fires
 * the credential callback with a fresh token and no visible UI. Resolves once
 * the prompt has been triggered (the actual token arrives via the callback).
 */
export async function requestSilentToken(callback) {
  await ensureGoogleLoaded();
  initGoogleAuth(callback);
  if (currentCallback == null) currentCallback = callback;
  else currentCallback = callback;
  if (window.google && window.google.accounts) {
    window.google.accounts.id.prompt();
  }
}
